import { useState } from "react";

import {
	useTradeMarket,
	useTradeOpenOrders,
	useTradeOrderBook,
	useTradeRecentTrades,
} from "../../hooks/use-trade-data.ts";
import { MARKET_TRADES_ROW_LIMIT } from "../market-trades/constants/market-trades.constants.ts";
import { MarketTradesTable } from "../market-trades/market-trades-table.tsx";
import { TradePanel } from "../trade-panel.tsx";
import { useTradeSelectedPrice } from "../trade-selected-price-context.tsx";
import {
	bucketIndexForOrder,
	type GroupingOption,
	getGroupingOptions,
	getGroupingStep,
	useOrderbook,
} from "./hooks/use-orderbook.ts";
import { useOrderbookRowCount } from "./hooks/use-orderbook-row-count.ts";
import { useOrderbookViewMode } from "./hooks/use-orderbook-view-mode.ts";
import { OrderbookControls, OrderbookHeader } from "./orderbook-header.tsx";
import { OrderbookTable } from "./orderbook-table.tsx";
import type { OrderbookSizeUnit } from "./types/orderbook.types.ts";

export function Orderbook() {
	const [activeTab, setActiveTab] = useState<"orderbook" | "trades">(
		"orderbook",
	);
	const { viewMode, setViewMode } = useOrderbookViewMode();
	const [sizeUnit, setSizeUnit] = useState<OrderbookSizeUnit>("usdc");
	const { marketData } = useTradeMarket();

	const groupingOptions = getGroupingOptions(marketData.markPrice);
	const [selectedIndex, setSelectedIndex] = useState(0);

	// Reset grouping when coin changes
	const [prevCoin, setPrevCoin] = useState(marketData.coin);
	if (prevCoin !== marketData.coin) {
		setPrevCoin(marketData.coin);
		setSelectedIndex(0);
	}

	const grouping: GroupingOption =
		groupingOptions[selectedIndex] ?? groupingOptions[0];

	const { book, displayedGrouping } = useTradeOrderBook(grouping);
	const { trades } = useTradeRecentTrades();
	const { openOrders } = useTradeOpenOrders();
	const { setSelectedPrice } = useTradeSelectedPrice();
	const { containerRef, rowCount } = useOrderbookRowCount(viewMode);

	// Always format/process levels under the grouping that produced them.
	// During a grouping change `book` is still the previous frame, so we must
	// also use the previous grouping until the new frame arrives. This is the
	// fix for the "blink with wrong decimals" bug.
	const formattingGrouping: GroupingOption = displayedGrouping ?? grouping;

	const orderbook = useOrderbook({
		book,
		trades,
		markPrice: marketData.markPrice,
		marketKey: marketData.coin,
		rowLimit: rowCount,
		grouping: formattingGrouping,
	});

	// React Compiler memoizes this — no useMemo needed.
	const orderMarkers = (() => {
		const bids = new Map<string, number>();
		const asks = new Map<string, number>();
		const step = getGroupingStep(formattingGrouping);

		// Map bucket index → raw API px string for visible levels. Keying on
		// bucket index avoids string-equality fragility between the API's
		// canonical px and any client-side reformat.
		const visibleBidsByBucket = new Map<number, string>();
		const visibleAsksByBucket = new Map<number, string>();
		for (const level of orderbook.bids) {
			visibleBidsByBucket.set(level.bucketIndex, level.px);
		}
		for (const level of orderbook.asks) {
			visibleAsksByBucket.set(level.bucketIndex, level.px);
		}

		for (const order of openOrders) {
			if (order.coin !== marketData.coin) continue;

			const side = order.side === "B" ? "bid" : "ask";
			const idx = bucketIndexForOrder(Number(order.limitPx), side, step);
			if (idx === null) continue;

			const visible =
				side === "bid" ? visibleBidsByBucket : visibleAsksByBucket;
			const px = visible.get(idx);
			if (!px) continue;

			const markers = side === "bid" ? bids : asks;
			markers.set(px, (markers.get(px) ?? 0) + 1);
		}

		return { bids, asks };
	})();

	return (
		<TradePanel className="border-0 sm:border">
			<OrderbookHeader activeTab={activeTab} onTabChange={setActiveTab} />
			{activeTab === "orderbook" ? (
				<>
					<OrderbookControls
						viewMode={viewMode}
						onViewModeChange={setViewMode}
						groupingOptions={groupingOptions}
						selectedGroupingIndex={selectedIndex}
						onGroupingChange={setSelectedIndex}
						coin={marketData.coin}
						sizeUnit={sizeUnit}
						onSizeUnitChange={setSizeUnit}
					/>
					<OrderbookTable
						ref={containerRef}
						coin={marketData.coin}
						bids={orderbook.bids}
						asks={orderbook.asks}
						bidMarkers={orderMarkers.bids}
						askMarkers={orderMarkers.asks}
						spread={orderbook.spread}
						spreadPercent={orderbook.spreadPercent}
						lastTradedPrice={orderbook.lastTradedPrice}
						tradePriceArrow={orderbook.tradePriceArrow}
						tradePriceClassName={orderbook.tradePriceClassName}
						priceDecimals={orderbook.priceDecimals}
						viewMode={viewMode}
						sizeUnit={sizeUnit}
						onPriceSelect={setSelectedPrice}
					/>
				</>
			) : (
				<MarketTradesTable
					trades={trades.slice(0, MARKET_TRADES_ROW_LIMIT)}
					coin={marketData.coin}
				/>
			)}
		</TradePanel>
	);
}
